import clsx from 'clsx';
import React from 'react';

import Comments from '@components/Comments';
import FadeWrapper from '@components/FadeWrapper';
import MostPopularArticles from '@components/MostPopularArticles';
import Paywall from '@components/Paywall';
import RelatedStories from '@components/RelatedStories';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';
import { LeadType } from '@utils/enums';

import { BriefingProps } from '.';
import Body from './Body';
import BriefingDetails from './BriefingDetails';
import ContinuedReadingContainer from './ContinuedReadingContainer';
import Headline from './Headline';

const BriefingContent = ({
  briefing: { authors, body, createdAt, commentCount, headline, id, relatedItems, source },
  canViewFullBriefing,
  isGooglebot,
  mostPopularArticles,
  paywallProps,
  relatedArticles,
  teaser,
}: BriefingProps) => {
  const hydrated = useServerSideRendering();
  const shouldShowPaywall = !canViewFullBriefing && !isGooglebot;
  const { Track } = useEventTracking({
    page: Page.Briefing,
    contentType: ContentType.Briefing,
    contentId: id,
  });

  return (
    <Track>
      <article className="relative pb-64 pt-32 sm:pb-112 sm:pt-48">
        {!canViewFullBriefing && (
          <div
            className="absolute bottom-0 left-0 top-0 z-40 w-full bg-black/30"
            data-testid="paywall-overlay"
          />
        )}
        <div className="px-16 sm:px-28">
          <div className="m-auto lg:max-w-[1250px] xl:max-w-container-lg">
            <Headline headline={headline} />
            <div className="block pb-48 pt-32 sm:pt-48 md:hidden">
              <BriefingDetails
                authors={authors}
                createdAt={createdAt}
                source={source}
                canViewFullBriefing={canViewFullBriefing}
              />
            </div>
            <div
              className={clsx('flex pt-0 md:gap-72 md:pt-64 xl:gap-112', {
                'pb-64 ': !canViewFullBriefing,
              })}
            >
              <div className="hidden flex-shrink-0 md:block md:w-[195px] lg:w-[260px]">
                <div className="flex flex-col">
                  <BriefingDetails
                    authors={authors}
                    createdAt={createdAt}
                    source={source}
                    canViewFullBriefing={canViewFullBriefing}
                  />
                  {canViewFullBriefing && (
                    <MostPopularArticles
                      mostPopularArticles={mostPopularArticles}
                      className="md:mt-80 xl:mt-[102px] xl:max-w-[230px]"
                    />
                  )}
                </div>
              </div>
              <div className="relative flex-1">
                {isGooglebot || canViewFullBriefing ? (
                  <Body body={body} isGooglebot={isGooglebot} />
                ) : (
                  <FadeWrapper>{teaser}</FadeWrapper>
                )}
                {canViewFullBriefing && (
                  <>
                    {relatedItems.length > 0 && (
                      <ContinuedReadingContainer relatedItems={relatedItems} />
                    )}
                    {hydrated && (
                      <div className="mt-80 sm:mt-112">
                        <Comments
                          commentableId={id}
                          commentableType={CommentableType.Briefing}
                          commentCount={commentCount}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <RelatedStories articles={relatedArticles} />
          </div>
        </div>
      </article>
      {shouldShowPaywall && hydrated && (
        <Paywall
          leadType={LeadType.BriefingLead}
          trackingProps={{ 'track-event': 'Paywall' }}
          {...paywallProps}
        />
      )}
    </Track>
  );
};

export default BriefingContent;
